import React, { useContext, useState } from 'react';
import { Spinner } from 'reactstrap';
import { MdTimer, MdOutlinePoll } from 'react-icons/md';
import { Language } from '../App';
import usePollRemainingTime from '../hooks/usePollRemainingTime';

const PollOptionsView = ({ value, handlePollAnswer, toggleModal }) => {
  const { lang } = useContext(Language);
  const [loadingIndex, setLoadingIndex] = useState(null);

  const userData = localStorage.getItem("SignupData");
  const user = userData ? JSON.parse(userData) : null;
  const user_id = user?.data?.id;

  function getPollStatus(timestamp, duration) {
    const pollStartTime = parseInt(timestamp, 10);
    if (isNaN(pollStartTime)) {
      return 'Invalid timestamp';
    }

    const currentTime = Math.floor(Date.now() / 1000);
    const durationInSeconds = convertDurationToSeconds(duration);
    if (durationInSeconds === 0) {
      return duration;
    }

    const pollEndTime = pollStartTime + durationInSeconds;

    if (currentTime < pollEndTime) {
      const remainingTimeInSeconds = pollEndTime - currentTime;
      return formatRemainingTime(remainingTimeInSeconds);
    } else {
      return lang.poll_closed || 'Poll closed';
    }
  }

  function convertDurationToSeconds(duration) {
    switch (duration) {
      case "1 Day":
        return 1 * 24 * 60 * 60;
      case "3 Days":
        return 3 * 24 * 60 * 60;
      case "7 Days":
        return 7 * 24 * 60 * 60;
      case "2 Weeks":
        return 14 * 24 * 60 * 60;
      default:
        return 0;
    }
  }

  function formatRemainingTime(seconds) {
    const days = Math.floor(seconds / (24 * 60 * 60));
    const hours = Math.floor((seconds % (24 * 60 * 60)) / (60 * 60));
    const minutes = Math.floor((seconds % (60 * 60)) / 60);

    if (days > 0) {
      return days === 1 ? `${days} ${lang.day_left || 'day left'}` : `${days} ${lang.days_left || 'days left'}`;
    }

    if (hours > 0) {
      return hours === 1 ? `${hours} ${lang.hour_left || 'hour left'}` : `${hours} ${lang.hours_left || 'hours left'}`;
    }

    if (minutes > 0) {
      return minutes === 1 ? `${minutes} ${lang.minute_left || 'minute left'}` : `${minutes} ${lang.minutes_left || 'minutes left'}`;
    }

    return lang.poll_closed || 'Poll closed';
  }

  const showVoteModal = (id) => {
    if (user_id === value?.user?.id) {
      toggleModal(id);
    }
  };

  const onOptionClick = async (index) => {
    if (isClosed || loadingIndex !== null) return;
    setLoadingIndex(index);
    try {
      await handlePollAnswer(value?.original_id, index);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingIndex(null);
    }
  };

  const remainingTime = usePollRemainingTime(value?.time, value?.duration);
  const pollStatus = getPollStatus(value?.time, value?.duration);
  const isClosed = pollStatus === lang.poll_closed || pollStatus === 'Poll closed';
  const optionLetters = ['A', 'B', 'C', 'D'];

  return (
    <div className="px-2 px-sm-4 py-2">
      <div className="d-flex flex-column mb-3" style={{ gap: "10px" }}>
        {[value?.option_1, value?.option_2, value?.option_3, value?.option_4]
          .filter((data) => data !== "" && data !== null && data !== undefined)
          .map((data, index) => (
            <div
              key={index}
              className="d-flex align-items-center justify-content-between p-3 rounded-3 feed-row-hover cursor position-relative overflow-hidden"
              onClick={() => onOptionClick(index)}
              style={{
                background: loadingIndex === index ? "#E0F7FA" : "#F8FAFC",
                border: loadingIndex === index ? "1.5px solid #00BADA" : "1.5px solid #E2E8F0",
                borderRadius: "14px",
                opacity: isClosed ? 0.6 : 1,
                cursor: isClosed ? "not-allowed" : "pointer",
                pointerEvents: isClosed || loadingIndex !== null ? "none" : "auto",
                transition: "all 0.2s ease",
              }}
            >
              <div className="d-flex align-items-center me-2" style={{ gap: "12px", minWidth: 0 }}>
                <span
                  className="d-flex align-items-center justify-content-center roboto-bold rounded-circle flex-shrink-0"
                  style={{
                    width: "30px",
                    height: "30px",
                    background: "#E0F7FA",
                    color: "#00BADA",
                    fontSize: "13px",
                  }}
                >
                  {optionLetters[index] || index + 1}
                </span>
                <span
                  className="roboto-medium fs-14 text-truncate"
                  style={{ color: "#0F172A" }}
                >
                  {data}
                </span>
              </div>
              <div className="flex-shrink-0 ms-2">
                {loadingIndex === index ? (
                  <Spinner size="sm" style={{ color: "#00BADA" }} />
                ) : (
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{
                      width: "20px",
                      height: "20px",
                      border: "2px solid #CBD5E1",
                      background: "#FFFFFF",
                    }}
                  />
                )}
              </div>
            </div>
          ))}
      </div>

      {/* Render vote count and remaining time */}
      <div className="d-flex align-items-center justify-content-between px-1 py-1">
        <div className="d-flex align-items-center gap-2">
          <MdOutlinePoll size={18} style={{ color: "#00BADA" }} />
          <span
            className="mb-0 roboto-bold fs-13 cursor hover-blue"
            onClick={() => showVoteModal(value.id)}
            style={{ color: "#00BADA" }}
          >
            {value.votes_count} {value.votes_count === 1 ? lang.vote || "vote" : lang.votes || "votes"}
          </span>
        </div>
        {value.duration && (
          <div className="d-flex align-items-center gap-1.5 px-3 py-1 rounded-pill" style={{ background: "#F1F5F9", border: "1px solid #E2E8F0" }}>
            <MdTimer size={14} style={{ color: "#64748B" }} />
            <span className="mb-0 fs-12 roboto-medium" style={{ color: "#64748B" }}>
              {remainingTime}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default PollOptionsView;
